// Frame-by-frame exporter. The MVP renders client-side: serialise the SVG at
// each keyframe time, rasterise to a <canvas>, capture via MediaRecorder.
//
// We DON'T go through the live DOM because MediaRecorder needs a captured
// stream from a canvas. Instead we mount a hidden DOM, apply the timeline at
// each frame time, serialise the SVG to a data URL, draw to canvas. Works in
// every modern browser; the only quirk is Safari ≤17's missing VP9, so we
// fall back to VP8 there.

import { applyAt } from "@app/shared/runtime";
import type { Timeline } from "@app/shared/ir";
import { measurePathLengths, applyResolution } from "./apply";

export interface ExportOpts {
  svg: string;
  timeline: Timeline;
  width: number;
  height: number;
  fps?: number;
  background?: string | "transparent";
  onProgress?: (p: number) => void;
}

export async function exportToBlob(opts: ExportOpts): Promise<{ blob: Blob; mime: string; ext: "webm" }> {
  const { svg, timeline, width, height, fps = 30, background = "transparent", onProgress } = opts;

  // Offscreen DOM host.
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-99999px";
  host.style.width = `${width}px`;
  host.style.height = `${height}px`;
  host.style.contain = "strict";
  host.innerHTML = svg;
  document.body.appendChild(host);

  const svgEl = host.querySelector("svg") as SVGSVGElement | null;
  if (!svgEl) { document.body.removeChild(host); throw new Error("invalid_svg"); }
  svgEl.setAttribute("width", String(width));
  svgEl.setAttribute("height", String(height));
  svgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");
  const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
  g.setAttribute("data-logo-root", "");
  while (svgEl.firstChild && svgEl.firstChild !== g) g.appendChild(svgEl.firstChild);
  svgEl.appendChild(g);
  measurePathLengths(host);

  // Canvas + stream.
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: background === "transparent" })!;

  const stream = canvas.captureStream(fps);

  // MIME selection. VP9 first for size/quality; fall back to VP8.
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  const mime = candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) ?? "video/webm";

  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
  const chunks: BlobPart[] = [];
  rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
  const stopped = new Promise<void>((resolve) => rec.onstop = () => resolve());
  rec.start(100); // small timeslice so we get chunks while drawing

  const totalFrames = Math.ceil((timeline.durationMs / 1000) * fps);
  const frameMs = 1000 / fps;

  try {
    for (let i = 0; i <= totalFrames; i++) {
      const t = Math.min(timeline.durationMs, i * frameMs);
      // Apply timeline state.
      const applied = applyAt(timeline, t);
      for (const a of applied) applyResolution(host, a);

      // Serialise SVG → image.
      // We snapshot computed transforms onto inline style so that <img src>
      // rasterises the current state (HTMLImage doesn't run rAF / scripts).
      const blobSvg = new Blob([new XMLSerializer().serializeToString(svgEl)], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blobSvg);
      const img = new Image();
      img.decoding = "sync";
      await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = (e) => rej(e); img.src = url; });

      if (background !== "transparent") {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);

      onProgress?.(i / totalFrames);

      // Pace the stream — captureStream samples on its own clock, so we wait
      // ~1 frame's worth of wall-clock between draws.
      await new Promise((r) => setTimeout(r, Math.max(8, frameMs - 4)));
    }
  } finally {
    rec.stop();
    await stopped;
    document.body.removeChild(host);
  }

  const blob = new Blob(chunks, { type: mime });
  return { blob, mime, ext: "webm" };
}
